import React, { FC, ReactElement, useEffect, useRef, useState } from "react";

// Product images
import img20l from "../../img/products/20l.jpg";
import img5l  from "../../img/products/5l.jpg";

// Partner logos
import p1 from "../../img/part/1.png";
import p2 from "../../img/part/2.png";
import p3 from "../../img/part/3.png";
import p4 from "../../img/part/4.png";
import p5 from "../../img/part/5.png";

import "./AboutPage.css";

const STATS = [
  { icon: "💧", value: "15K+", label: "Litres / Day"  },
  { icon: "🏪", value: "250+", label: "Outlets"       },
  { icon: "👥", value: "12K+", label: "Customers"     },
  { icon: "🏭", value: "5+",   label: "Plants"        },
  { icon: "🏗️", value: "6",   label: "Upcoming"      },
  { icon: "🚛", value: "26",   label: "Vehicles"      },
  { icon: "🏢", value: "10",   label: "Stock Yards"   },
  { icon: "🤝", value: "20",   label: "Distributors"  },
];

const FEATURES = [
  { icon: "💧", title: "100% Pure",     sub: "Tested & Certified" },
  { icon: "🚚", title: "Fast Delivery", sub: "Always On Time"     },
  { icon: "💰", title: "Best Prices",   sub: "No Hidden Costs"    },
  { icon: "🎧", title: "Quick Support", sub: "Always Here"        },
];

const QUALITY = [
  {
    icon: "✅",
    title: "Maximum Purity",
    desc: "Water is treated to the highest levels of purity through our advanced manufacturing process, ensuring every drop meets international safety standards.",
  },
  {
    icon: "🧪",
    title: "10 Step Process",
    desc: "Taste Adjuster · RO Maxx Technology · UVE Boiling · Ultra Filtration · 2-in-1 Mineral · Ozonation · Aquasaver Technology · Sediment Filter · Particulate Filter · Chemi Block",
  },
  {
    icon: "🏅",
    title: "Quality Certified",
    desc: "Our certifications and quality badges reflect compliance with water safety standards determined by leading certification organizations across India.",
  },
];

const SERVICES = [
  { icon: "🏠", title: "Home Delivery",  desc: "Fresh water delivered directly to your home. Perfect for families who value health and convenience."  },
  { icon: "🏢", title: "Office Supply",  desc: "Bulk delivery for offices and businesses. Keep your team hydrated with reliable service."              },
  { icon: "🏥", title: "Healthcare",     desc: "Specialized delivery for hospitals and clinics with strict quality standards."                          },
  { icon: "🍽️", title: "Restaurants",  desc: "Premium water supply for restaurants and food service establishments."                                  },
  { icon: "⚡",  title: "Emergency",     desc: "Round-the-clock emergency delivery ensures you're never without water when you need it most."           },
  { icon: "📅", title: "Subscriptions", desc: "Flexible subscription plans for regular deliveries at discounted rates."                                 },
];

const PARTNERS = [p1, p2, p3, p4, p5, p1, p2, p3, p4, p5];

const AboutPage: FC = (): ReactElement => {
  const [counted, setCounted] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCounted(true); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="ab-page">

      {/* ══ HERO ══════════════════════════════════════════════ */}
      <section className="ab-hero">
        <div className="ab-blob ab-blob-1" />
        <div className="ab-blob ab-blob-2" />
        <div className="ab-container ab-hero-grid">

          <div className="ab-hero-left">
            <div className="ab-live-badge">
              <span className="ab-live-dot" />
              <span>Pure Water · Fast Delivery</span>
            </div>
            <h1 className="ab-hero-title">
              Fresh &amp; Pure
              <span className="ab-grad ab-hero-accent"> Water Delivered</span>
              <br />To Your Door
            </h1>
            <p className="ab-hero-sub">
              Experience premium water manufacturing with guaranteed purity,
              lightning-fast delivery, and unmatched reliability for homes and
              businesses across Bengaluru.
            </p>
            <div className="ab-hero-stats">
              <div className="ab-hero-stat">
                <span className="ab-hero-stat-val">12K+</span>
                <span className="ab-hero-stat-label">Happy Customers</span>
              </div>
              <div className="ab-hero-stat-div" />
              <div className="ab-hero-stat">
                <span className="ab-hero-stat-val">15K+</span>
                <span className="ab-hero-stat-label">Bottles Daily</span>
              </div>
              <div className="ab-hero-stat-div" />
              <div className="ab-hero-stat">
                <span className="ab-hero-stat-val">100%</span>
                <span className="ab-hero-stat-label">Pure Water</span>
              </div>
            </div>
          </div>

          <div className="ab-hero-right">
            <div className="ab-hero-float">
              <img src={img20l} alt="20L Finllie bottle" className="ab-hero-img" />
            </div>
            <div className="ab-delivery-badge">
              <div className="ab-delivery-icon">🚚</div>
              <div>
                <div className="ab-delivery-title">Fast Delivery</div>
                <div className="ab-delivery-sub">Under 2 Hours</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ══ FEATURES STRIP ════════════════════════════════════ */}
      <section className="ab-features-strip">
        <div className="ab-container ab-features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="ab-feature">
              <div className="ab-feature-icon">{f.icon}</div>
              <div>
                <div className="ab-feature-title">{f.title}</div>
                <div className="ab-feature-sub">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ WHO WE ARE ════════════════════════════════════════ */}
      <section className="ab-section ab-white">
        <div className="ab-container ab-two-col">
          <div className="ab-img-wrap ab-floating">
            <img src={img20l} alt="Finllie 20L" className="ab-img" />
            <div className="ab-img-badge">💧 10-Step Quality</div>
          </div>
          <div className="ab-text-block">
            <span className="ab-pill">Who We Are</span>
            <h2 className="ab-section-title">
              We Provide All Kinds of{" "}
              <span className="ab-grad">Residential &amp; Commercial Water Solutions</span>
            </h2>
            <p className="ab-body">
              Finllie is a promise of purity that goes through a rigorous 10-step
              quality process. The water is safe, pure, and hygienic. First time in
              India, we are providing water through hospitals and medical shops —
              that's our promise to our customers.
            </p>
            <p className="ab-body">
              Finllie mineral water is not just ordinary water. Each drop is a promise
              of purity. It adds essential minerals to ensure your health and wellness.
            </p>
          </div>
        </div>
      </section>

      {/* ══ QUALITY ASSURANCE ═════════════════════════════════ */}
      <section className="ab-section ab-light">
        <div className="ab-container">
          <div className="ab-section-head">
            <span className="ab-pill">Quality Assurance</span>
            <h2 className="ab-section-title">
              India's 1st &amp; Only Patented Cans —{" "}
              <span className="ab-grad">Available in Different Sizes with Smart Colour Coding</span>
            </h2>
            <p className="ab-section-sub">
              Our commitment to excellence is backed by certifications and rigorous testing
            </p>
          </div>
          <div className="ab-quality-grid">
            {QUALITY.map((q, i) => (
              <div key={i} className="ab-quality-card">
                <div className="ab-quality-icon-wrap">{q.icon}</div>
                <h3 className="ab-quality-title">{q.title}</h3>
                <p className="ab-body">{q.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ STATS ═════════════════════════════════════════════ */}
      <section className="ab-section ab-white" ref={statsRef}>
        <div className="ab-container">
          <div className="ab-section-head">
            <span className="ab-pill">Our Impact</span>
            <h2 className="ab-section-title">
              Growing <span className="ab-grad">Every Day</span>
            </h2>
            <p className="ab-section-sub">
              Numbers that reflect our commitment to quality and service
            </p>
          </div>
          <div className="ab-stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="ab-stat-card" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="ab-stat-icon">{s.icon}</div>
                <div className={`ab-stat-val ${counted ? "ab-counted" : ""}`}>{s.value}</div>
                <div className="ab-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MARKET POTENTIAL ══════════════════════════════════ */}
      <section className="ab-section ab-light">
        <div className="ab-container ab-two-col ab-rev">
          <div className="ab-text-block">
            <span className="ab-pill">Market Potential</span>
            <h2 className="ab-section-title">
              Meeting the Growing <span className="ab-grad">Demand</span>
            </h2>
            <p className="ab-body">
              Safe portable water is not available everywhere in the country. Either
              harmful chemical substances are found in the layer of earth which enter
              into water, or it may be contaminated due to pathogenic micro-organisms.
              It has become imperative to process and bottle safe portable water for
              mankind.
            </p>
            <p className="ab-body">
              The product is widely accepted in offices, restaurants, supermarkets,
              hospitals, medical shops, and premium residences — there is enormous
              scope for establishing quality water distribution units across every
              metropolitan city and town.
            </p>
          </div>
          <div className="ab-img-wrap">
            <img src={img5l} alt="Finllie 5L" className="ab-img" />
            <div className="ab-img-glow" />
          </div>
        </div>
      </section>

      {/* ══ MISSION & VISION ══════════════════════════════════ */}
      <section className="ab-section ab-white">
        <div className="ab-container">
          <div className="ab-section-head">
            <span className="ab-pill">Our Purpose</span>
            <h2 className="ab-section-title">
              Mission &amp; <span className="ab-grad">Vision</span>
            </h2>
          </div>
          <div className="ab-mv-grid">
            <div className="ab-mv-card">
              <div className="ab-mv-icon">🧭</div>
              <h3 className="ab-mv-title">Our Mission</h3>
              <p className="ab-body">
                To provide the highest quality purified and mineralized drinking water
                through our rigorous 10-step quality process, ensuring every drop is
                safe, pure, and hygienic for customers across Bengaluru and beyond.
              </p>
            </div>
            <div className="ab-mv-card">
              <div className="ab-mv-icon">👁️</div>
              <h3 className="ab-mv-title">Our Vision</h3>
              <p className="ab-body">
                To be India's most trusted water delivery brand, pioneering innovative
                distribution channels including hospitals and medical shops, while
                bridging the gap between demand and supply of safe portable water
                across the nation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SERVICES ══════════════════════════════════════════ */}
      <section className="ab-section ab-light">
        <div className="ab-container">
          <div className="ab-section-head">
            <span className="ab-pill">Our Services</span>
            <h2 className="ab-section-title">
              Services We <span className="ab-grad">Provide</span>
            </h2>
            <p className="ab-section-sub">
              Comprehensive water delivery solutions for every need
            </p>
          </div>
          <div className="ab-services-grid">
            {SERVICES.map((s, i) => (
              <div key={i} className="ab-service-card">
                <div className="ab-service-icon-wrap">{s.icon}</div>
                <h3 className="ab-service-title">{s.title}</h3>
                <p className="ab-body">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TRUSTED PARTNERS ══════════════════════════════════ */}
      <section className="ab-section ab-white ab-partners-section">
        <div className="ab-container">
          <div className="ab-section-head">
            <span className="ab-pill">Who We Serve</span>
            <h2 className="ab-section-title">
              Trusted <span className="ab-grad">Partners</span>
            </h2>
            <p className="ab-section-sub">
              Widely accepted in offices, restaurants, supermarkets, hospitals,
              medical shops and premium residences.
            </p>
          </div>
        </div>
        <div className="ab-marquee-wrap">
          <div className="ab-mfade ab-mfade-l" />
          <div className="ab-mfade ab-mfade-r" />
          <div className="ab-marquee-track">
            {PARTNERS.map((src, i) => (
              <div key={i} className="ab-marquee-item">
                <img src={src} alt={`Partner ${(i % 5) + 1}`} className="ab-partner-logo" />
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;